import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, TrendingUp, Eye, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/Primitives";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { CAREERS_FAQ } from "@/lib/content";

const PROPS = [
  { icon: ShieldCheck, title: "Absolute discretion", desc: "We never share your details without your explicit, role-by-role consent." },
  { icon: Eye, title: "Access the hidden market", desc: "The best roles are never advertised. We open doors inside Monaco & Geneva." },
  { icon: TrendingUp, title: "Real career guidance", desc: "Advice from people who built front-office banking careers themselves." },
];
const inputClass = "h-12 rounded-[var(--radius-sm)] border-[color:var(--bp-border)] bg-[color:var(--bp-bg-2)] text-[color:var(--bp-cream)] placeholder:text-[color:var(--bp-text-muted)]/60";
const labelClass = "text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bp-text-muted)]";

export default function Careers() {
  const [form, setForm] = useState({ name: "", email: "", role: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    if (!form.consent) { toast.error("Please confirm your consent."); return; }
    setLoading(true);
    try {
      await api.createLead({ type: "candidate", ...form });
      toast.success("Received in confidence", { description: "We'll be in touch when the right opportunity appears." });
      setForm({ name: "", email: "", role: "", message: "", consent: false });
    } catch (err) { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="careers-page">
      <PageHero
        breadcrumb={[{ label: "Careers" }]}
        eyebrow="For candidates"
        title="The best roles never reach a job board."
        intro="If you're a senior banking professional in Monaco, Geneva or Paris — actively looking or not — a confidential conversation now means we understand your situation when the right opportunity appears."
      />

      <section className="section-py pt-8">
        <div className="container-bp">
          <RevealGroup className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {PROPS.map((p) => (
              <RevealItem key={p.title}>
                <div className="h-full rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-7">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[color:var(--bp-teal)]/40 bg-[color:var(--bp-teal-soft)] text-[color:var(--bp-teal)]"><p.icon className="h-5 w-5" /></span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-[color:var(--bp-cream)]">{p.title}</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-[color:var(--bp-text-muted)]">{p.desc}</p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <section className="section-py bg-[color:var(--bp-bg-2)]">
        <div className="container-bp grid grid-cols-1 gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-7 sm:p-9">
              <h2 className="font-display text-2xl font-semibold text-[color:var(--bp-cream)]">Confidential introduction</h2>
              <p className="mt-2 text-[14px] text-[color:var(--bp-text-muted)]">Start a discreet conversation. Submit a CV any time via our Tools page.</p>
              <form onSubmit={submit} className="mt-7 space-y-5" data-testid="careers-form">
                <div className="space-y-2"><Label className={labelClass}>Full name</Label><Input data-testid="careers-name" className={inputClass} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" /></div>
                <div className="space-y-2"><Label className={labelClass}>Email</Label><Input data-testid="careers-email" type="email" className={inputClass} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" /></div>
                <div className="space-y-2"><Label className={labelClass}>Current role</Label><Input data-testid="careers-role" className={inputClass} value={form.role} onChange={(e) => set("role", e.target.value)} placeholder="e.g. Senior Relationship Manager" /></div>
                <div className="space-y-2"><Label className={labelClass}>What are you looking for?</Label><Textarea data-testid="careers-message" rows={3} className={inputClass + " h-auto py-3"} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Optional — tell us in confidence." /></div>
                <label className="flex cursor-pointer items-start gap-3 text-[13px] text-[color:var(--bp-text-muted)]">
                  <Checkbox data-testid="careers-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5 border-[color:var(--bp-border)] data-[state=checked]:bg-[color:var(--bp-teal)] data-[state=checked]:border-[color:var(--bp-teal)]" />
                  <span>I consent to a confidential conversation with Beyond Potentials.</span>
                </label>
                <button type="submit" disabled={loading} data-testid="careers-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-[color:var(--bp-teal)] px-6 py-4 text-sm font-semibold text-[color:var(--bp-bg)] transition-shadow hover:shadow-[var(--shadow-lift)] disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {loading ? "Sending…" : "Start a confidential conversation"} {!loading && <ArrowRight className="h-4 w-4" />}
                </button>
              </form>
            </div>
          </Reveal>
          <div>
            <SectionHeading eyebrow="Good to know" title="Your questions, answered." />
            <Reveal>
              <Accordion type="single" collapsible defaultValue="q-0" className="mt-6">
                {CAREERS_FAQ.map((f, i) => (
                  <AccordionItem key={i} value={`q-${i}`} className="border-[color:var(--bp-border)]">
                    <AccordionTrigger className="text-left font-display text-base font-semibold text-[color:var(--bp-cream)] hover:text-[color:var(--bp-teal)] hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[14px] leading-relaxed text-[color:var(--bp-text-muted)]">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
